import * as ngCdkOverlay from '@angular/cdk/overlay'
import * as ngCore from '@angular/core'

/**
 * A slideout overlay component that can be used to display a dialog. By
 * default, it is positioned on the right side of the page, but its position can
 * be overridden using an input property.
 *
 * #### Slots
 * - `actions` Used for projecting additional content next to the slideout name.
 * - *`default`* Used for the main overlay content.
 *
 * #### Inputs
 * - {@link Component.hasBackdrop|hasBackdrop}?
 * - {@link Component.backdropClass|backdropClass}?
 * - {@link Component.position|position}?
 * - {@link Component.name|name}?
 *
 * #### Outputs
 * - {@link Component.backdropClicked|backdropClicked}
 *
 * @example
 * ```html
 * <!-- Simple example -->
 * <atn-ds-slideout-overlay name="Example Slideout" *ngIf="condition">
 *   <div>Overlay content</div>
 * </atn-ds-slideout-overlay>
 *
 * <!-- Example with extra content projected next to the name of the slideout -->
 * <atn-ds-slideout-overlay name="Example Slideout" *ngIf="condition">
 *   <div>Overlay content</div>
 *   <div class="extra-content" actions>Extra content</div>
 * </atn-ds-slideout-overlay>
 *
 * <!-- Example with darkened backdrop -->
 * <atn-ds-slideout-overlay
 *   name="Example Slideout"
 *   backdropClass="ds-global-overlay-backdrop"
 *   *ngIf="condition"
 * >
 *   <div>Overlay content</div>
 * </atn-ds-slideout-overlay>
 * ```
 */
@ngCore.Component({
  imports: [ngCdkOverlay.OverlayModule],
  selector: 'atn-ds-slideout-overlay',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link ngCdkOverlay.Overlay} */
  private readonly _overlay = ngCore.inject(ngCdkOverlay.Overlay)
  /** `body` element of the DOM */
  public readonly body = ngCore.inject(ngCore.DOCUMENT)?.body

  /** @see {@link ngCdkOverlay.CdkConnectedOverlay} */
  private readonly _connectedOverlaySg =
    ngCore.viewChild<ngCdkOverlay.CdkConnectedOverlay>(
      ngCdkOverlay.CdkConnectedOverlay,
    )

  /**
   * Whether the slideout has a backdrop.
   *
   * @default false
   */
  public hasBackdropSg = ngCore.input<boolean>(false, {
    alias: 'hasBackdrop',
  })

  /**
   * The class to apply to the backdrop.
   *
   * @default 'ds-global-overlay-backdrop--transparent'
   */
  public backdropClassSg = ngCore.input<string>(
    'ds-global-overlay-backdrop--transparent',
    {
      alias: 'backdropClass',
    },
  )

  /**
   * Event emitted when the backdrop is clicked.
   */
  public backdropClicked = ngCore.output<MouseEvent>()

  /**
   * Dialog name
   *
   * @default ''
   */
  public nameSg = ngCore.input<string | undefined>('', { alias: 'name' })

  /**
   * Flag that indicates whether slideout overlay has full height Note: should
   * be passed as an input before 'position' input
   *
   * @default false
   *
   * NOTE: Even though navigation has moved to the root navbar,
   * apps that support scenarios still keep their header to show
   * scenario-related features. For these apps, slideouts must
   * account for the header height when calculating their own height.
   */
  public hasFullHeightSg = ngCore.input<boolean | undefined>(true, {
    alias: 'hasFullHeight',
  })

  /**
   * The offset along the x-axis of the overlay connection point.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public offsetXSg = ngCore.input<number | undefined>(undefined, {
    alias: 'offsetX',
  })

  /**
   * The offset along the y-axis of the overlay connection point.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public offsetYSg = ngCore.input<number | undefined>(undefined, {
    alias: 'offsetY',
  })

  /**
   * The position of the dialog.
   *
   * @default 'r'
   */
  public positionSg = ngCore.input<'l' | 'r'>('r', {
    alias: 'position',
  })

  /**
   * Defines the height of the slideout overlay, if {@link hasFullHeightSg} is
   * true, height is 100vh, otherwise is subtract the header height from the
   * viewport height.
   */
  public slideoutOverlayHeightSg = ngCore.computed(() =>
    this.hasFullHeightSg() ? '100vh' : 'calc(100vh - 60px)',
  )

  /**
   * Computes the position strategy for the slideout overlay.
   */
  public overlayPositionSg = ngCore.computed(
    (
      offsetX = this.offsetXSg(),
      offsetY = this.offsetYSg(),
      position = this.positionSg(),
      hasFullHeight = this.hasFullHeightSg(),
    ) =>
      this._overlay
        .position()
        .flexibleConnectedTo(this.body)
        .withPositions([
          {
            offsetX,
            // 60px which is height of navigation links at the top of the page
            // TODO Remove conditional offset once navigation is migrated to
            // root navbar. After migration, offsetY will be the only way to
            // control vertical position.
            offsetY: offsetY ?? (hasFullHeight ? undefined : 60),
            originX: position === 'r' ? 'end' : 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
  )

  constructor() {
    // When position strategy inputs change while overlay is open,
    // we need to manually update the position strategy and trigger
    // position recalculation. This is because CDK overlay doesn't
    // automatically react to position strategy changes after initial render.
    ngCore.effect(() => {
      const overlay = this._connectedOverlaySg()
      if (!overlay) {
        return undefined
      }

      const position = this.overlayPositionSg()
      // NOTE: Safety check for overlayRef. Despite TypeScript types
      // suggesting it's always defined, there's a race condition during
      // initialization where it can be temporarily undefined
      if (overlay.overlayRef) {
        overlay.overlayRef.updatePositionStrategy(position)
        overlay.overlayRef.updatePosition()
      }
    })
  }
}
